"use client";
import React, { useEffect, useRef } from 'react';
import { isRecitationMeeting } from '@/app/lib/meetingTypes';
import { EyeIcon, MagnifyingGlassIcon, ClipboardIcon, ShareIcon, UserIcon, ClockIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { ScheduledCourse } from '../types/course';
import { useRouter } from 'next/navigation';
import { useUiPreferences } from '@/app/Components/UiPreferencesProvider';
import { buildAcademicBadgeStyle, buildAcademicSolidStyle, getCourseAcademicSwatch } from '@/app/lib/academicPalette';
import RoomLocationLink from '@/app/Components/RoomLocationLink';
import { formatBaseIdDisplay } from '@/app/lib/utils';

interface CourseDetailsPopupProps {
  isOpen: boolean;
  course: ScheduledCourse | null;
  x: number;
  y: number;
  onClose: () => void;
  onAddToSniper?: (course: ScheduledCourse) => void;
  onViewInExplore?: (course: ScheduledCourse) => void;
  onCopyCourseInfo?: (course: ScheduledCourse) => void;
  onShareCourse?: (course: ScheduledCourse) => void;
}

const CourseDetailsPopup: React.FC<CourseDetailsPopupProps> = ({
  isOpen,
  course,
  x,
  y,
  onClose,
  onAddToSniper,
  onViewInExplore,
  onCopyCourseInfo,
  onShareCourse
}) => {
  const router = useRouter();
  const { academicPalettePreferences, academicPalettePresets } = useUiPreferences();
  const popupRef = useRef<HTMLDivElement>(null);
  const courseIdentity = getCourseAcademicSwatch(
    course?.baseId || course?.id,
    academicPalettePreferences.coursePaletteId,
    academicPalettePresets
  );
  const normalizedBaseId = courseIdentity.normalizedBaseId;
  const courseBadgeStyle = courseIdentity.kind === 'accent'
    ? buildAcademicBadgeStyle(courseIdentity.swatch)
    : { backgroundColor: 'var(--ss-surface-subtle)', borderColor: 'var(--ss-border)', color: 'var(--ss-text)' };
  const courseDotStyle = courseIdentity.kind === 'accent'
    ? buildAcademicSolidStyle(courseIdentity.swatch)
    : { backgroundColor: 'var(--ss-surface-subtle)', color: 'var(--ss-text)' };


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  const handleActionClick = (action: () => void) => {
    action();
    onClose();
  };

  if (!isOpen || !course) return null;

  // Get meeting times with locations for this course
  const meetingTimesWithLocations = course.meetingTimes || [];
  const primaryLocation = course.location || meetingTimesWithLocations[0]?.location || 'TBA';
  const campusDisplay = course.campuses && course.campuses.length > 0
    ? course.campuses.join(', ')
    : course.campus || 'TBA';
  const campusDisplayLower = campusDisplay.toLowerCase();
  const instructors = course.instructors?.map(instructor => 
    typeof instructor === 'string' ? instructor : instructor.name
  ).filter(instructor => instructor && instructor !== 'TBA').join(', ') || 'TBA';
  const meetingSummary = meetingTimesWithLocations.length > 0
    ? meetingTimesWithLocations.map((mt) => {
        const dayLabel = mt.day && mt.day !== 'TBA' ? mt.day : 'TBA';
        const startLabel = typeof mt.startMinute === 'number' ? formatTime(mt.startMinute) : 'TBA';
        const endLabel = typeof mt.endMinute === 'number' ? formatTime(mt.endMinute) : 'TBA';
        const locationLabel = mt.location && mt.location !== 'TBA' ? ` @ ${mt.location}` : '';
        const campusLabel = mt.campus && mt.campus.length > 0 && !campusDisplayLower.includes(mt.campus.toLowerCase())
          ? ` (${mt.campus})`
          : '';
        return `${isRecitationMeeting(mt) ? 'Recitation · ' : ''}${dayLabel} ${startLabel} - ${endLabel}${locationLabel}${campusLabel}`;
      }).join(', ')
    : 'Meeting times TBA';

  return (
    <div
      ref={popupRef}
      className="fixed z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-control shadow-e2 py-2 min-w-64 max-w-sm"
      style={{
        left: x,
        top: y,
        transform: 'translate(-50%, -100%) translateY(-10px)'
      }}
    >
      {/* Course Info Header */}
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 mb-2">
        <div
          className="inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold break-words"
          style={courseBadgeStyle}
        >
          {courseIdentity.kind === 'accent' ? <span className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full" style={courseDotStyle} /> : null}
          <span className="truncate">{course.name}</span>
        </div>
        <div className="text-xs text-slate-600 dark:text-slate-400 break-words">
          {formatBaseIdDisplay(course.baseId)} • Section {course.section} • {campusDisplay} • {course.credits} credits
        </div>
      </div>

      {/* Menu Items */}
      <div className="py-1">
        {normalizedBaseId && (
          <button
            onClick={() => handleActionClick(() => router.push(`/courses/${encodeURIComponent(normalizedBaseId)}`))}
            className="w-full px-4 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-3 group"
          >
            <div className="text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white">
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900 dark:text-white">
                Open Course Page
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                View the full course profile and section history
              </div>
            </div>
          </button>
        )}

        {onAddToSniper && (
          <button
            onClick={() => handleActionClick(() => onAddToSniper(course))}
            className="w-full px-4 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-3 group"
          >
            <div className="text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white">
              <EyeIcon className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900 dark:text-white">
                Add to Course Sniper
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Monitor this course for open sections
              </div>
            </div>
          </button>
        )}

        {onViewInExplore && (
          <button
            onClick={() => handleActionClick(() => onViewInExplore(course))}
            className="w-full px-4 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-3 group"
          >
            <div className="text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white">
              <MagnifyingGlassIcon className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900 dark:text-white">
                Course details
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Ratings, sections, and prerequisites, with this section first
              </div>
            </div>
          </button>
        )}

        {onCopyCourseInfo && (
          <button
            onClick={() => handleActionClick(() => onCopyCourseInfo(course))}
            className="w-full px-4 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-3 group"
          >
            <div className="text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white">
              <ClipboardIcon className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900 dark:text-white">
                Copy Course Info
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Copy course details to clipboard
              </div>
            </div>
          </button>
        )}

        {onShareCourse && (
          <button
            onClick={() => handleActionClick(() => onShareCourse(course))}
            className="w-full px-4 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-3 group"
          >
            <div className="text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white">
              <ShareIcon className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900 dark:text-white">
                Share Course
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Share course with friends
              </div>
            </div>
          </button>
        )}
      </div>

      {/* Quick Info Section */}
      <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 mt-2">
        <div className="text-xs text-slate-600 dark:text-slate-400 mb-2">Quick Info:</div>
        <div className="space-y-1 text-xs">
          {instructors && instructors !== 'TBA' && (
            <div className="flex items-center space-x-2">
              <UserIcon className="h-3 w-3 text-slate-400" />
              <span className="text-slate-700 dark:text-slate-300">
                {instructors}
              </span>
            </div>
          )}
          {meetingTimesWithLocations && meetingTimesWithLocations.length > 0 && (
            <div className="flex items-center space-x-2">
              <ClockIcon className="h-3 w-3 text-slate-400" />
              <span className="text-slate-700 dark:text-slate-300">
                {meetingSummary}
              </span>
            </div>
          )}
          {primaryLocation && primaryLocation !== 'TBA' && (
            <div className="flex items-center space-x-2">
              <RoomLocationLink location={primaryLocation} className="text-slate-700 dark:text-slate-300" />
              {campusDisplay && campusDisplay !== 'TBA' ? <span className="text-slate-500 dark:text-slate-400">• {campusDisplay}</span> : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseDetailsPopup;
